import React, { Component } from "react";
import { Modal, Form, Input, Select, notification } from "antd";
import Axios from "axios";
import {
  FETAL_MOVEMENT,
  PRESENTATION,
  RELATIONSHIP_TO_PP_BRIM,
  Save_Mother_Fetus_Data,
  LOADER,
} from "../../../utils/constant";
import LS_SERVICE from "../../../utils/localStorage";

const { Option } = Select;

/**
 * FetusModal
 *
 * Renders one form block PER fetus (Fetus 1, Fetus 2, ... up to No. of Fetus). Each
 * block has its own fields with a MANDATORY Fetal Heart Rate. Save persists all fetuses
 * at once, keyed by fetus_no, preserving existing row ids so the backend (which deletes
 * rows whose id is not in the incoming set) updates in place instead of duplicating.
 *
 * Props:
 * - visible (bool)          : controls modal visibility
 * - onClose ()              : called when the modal is closed/cancelled
 * - allowEdit (bool)        : whether fields are editable (default true)
 * - noOfFetus (num)         : pregnancy's declared No. of Fetus (drives how many blocks)
 * - existingFetusData (arr) : the current fetus rows (latest per fetus_no) from the API
 * - onSaved () (optional)   : called after a successful save (to refresh the display)
 */
class FetusModal extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      allowEdit: props.allowEdit !== undefined ? props.allowEdit : true,
      spin_loading: false,
      // Only show the mandatory-FHR error after the user attempts to save, not on open.
      submitted: false,
      // One entry per fetus, keyed by array index (fetus_no = index + 1).
      fetuses: [],
    };
  }

  componentDidMount() {
    this.setState({ fetuses: this.buildInitialFetuses() });
  }

  buildWhereCondition = () => ({
    patient_id: parseInt(LS_SERVICE.get("PATIENT_REG_DATA").registrationid),
    reference_id: parseInt(LS_SERVICE.get("call_patient_qms_token_id")),
    reference_type: 0,
    hospital_id: parseInt(LS_SERVICE.get("FACILITY_ID")),
    enterprise_id: parseInt(LS_SERVICE.get("ENTERPRISE_ID")),
    source: "OP",
  });

  // Existing fetus rows keyed by fetus_no (fall back to 1-based position for legacy rows).
  normalizedExisting = () => {
    const rows = Array.isArray(this.props.existingFetusData) ? this.props.existingFetusData : [];
    // If several rows share a fetus_no (history), keep the latest (highest id).
    const latestByNo = {};
    rows.forEach((r, idx) => {
      const no = (r.fetus_no != null && r.fetus_no !== "") ? parseInt(r.fetus_no) : (idx + 1);
      const existing = latestByNo[no];
      if (!existing || ((r.id || 0) > (existing.id || 0))) {
        latestByNo[no] = {
          id: r.id || null,
          fetus_no: no,
          fetal_movement: r.fetal_movement,
          fetal_heart_rate: r.fetal_heart_rate,
          presentation: r.presentation,
          relationship_of_pp_to_brim: r.relationship_of_pp_to_brim,
          head_cc: r.head_cc,
          abdominal_cc: r.abdominal_cc,
          femur_length: r.femur_length,
        };
      }
    });
    return latestByNo;
  };

  // How many fetus blocks to render: the declared No. of Fetus, else the highest
  // existing fetus number, else 1.
  fetusCount = () => {
    const latestByNo = this.normalizedExisting();
    const existingNos = Object.keys(latestByNo).map(k => parseInt(k));
    const maxExisting = existingNos.length > 0 ? Math.max(...existingNos) : 0;
    const declared = parseInt(this.props.noOfFetus);
    if (!isNaN(declared) && declared > 0) return declared;
    if (maxExisting > 0) return maxExisting;
    return 1;
  };

  // Build one editable fetus object per fetus_no, prefilled from existing data.
  buildInitialFetuses = () => {
    const latestByNo = this.normalizedExisting();
    const count = this.fetusCount();
    // Start with blank blocks: each save records a NEW reading per fetus, so we do not
    // prefill from previous data.
    const list = [];
    for (let no = 1; no <= count; no++) {
      list.push({
        fetus_no: no,
        fetal_movement: null,
        fetal_heart_rate: null,
        presentation: null,
        relationship_of_pp_to_brim: null,
        head_cc: null,
        abdominal_cc: null,
        femur_length: null,
      });
    }
    return list;
  };

  updateFetusField = async (index, field, value) => {
    await this.setState((prevState) => {
      const fetuses = prevState.fetuses.map((f, i) => (i === index ? { ...f, [field]: value } : f));
      return { fetuses };
    });
  };

  handleFieldChange = (index, field) => (event) => {
    this.updateFetusField(index, field, event?.target?.value);
  };

  handleSelectChange = (index, field) => (value) => {
    this.updateFetusField(index, field, value);
  };

  // First fetus (1-based) missing FHR; 0 when all are filled.
  firstInvalidFhr = () => {
    const list = this.state.fetuses || [];
    for (let i = 0; i < list.length; i++) {
      const fhr = list[i]?.fetal_heart_rate;
      if (fhr === null || fhr === undefined || String(fhr).trim() === "") {
        return i + 1;
      }
    }
    return 0;
  };

  onFinish = async () => {
    // Mark as submitted so inline FHR errors can surface if validation fails.
    await this.setState({ submitted: true });
    // Fetal Heart Rate is mandatory for every fetus.
    const invalid = this.firstInvalidFhr();
    if (invalid > 0) {
      notification.error({ message: "Fetal Heart Rate is required for Fetus " + invalid + " to save", placement: "topRight" });
      return;
    }

    await this.setState({ spin_loading: true });

    // Each fetus is saved as a NEW reading (no id) so previous readings are preserved
    // as history. force_new tells the backend to append instead of delete-and-replace.
    const mother_fetal_parameters = (this.state.fetuses || []).map((f) => ({
      fetus_no: f.fetus_no,
      fetal_movement: f.fetal_movement,
      fetal_heart_rate: f.fetal_heart_rate,
      presentation: f.presentation,
      relationship_of_pp_to_brim: f.relationship_of_pp_to_brim,
      head_cc: f.head_cc,
      abdominal_cc: f.abdominal_cc,
      femur_length: f.femur_length,
    }));

    const mother_fetus_data = {
      mother_fetal_parameters,
      // Append new rows for this reference id (do not delete existing history).
      force_new: true,
      saved_by_doctor_id: LS_SERVICE.get("staff_id"),
      saved_by_doctor_name: LS_SERVICE.get("profile")?.data?.full_name || ("Dr. " + LS_SERVICE.get("staff_name")),
      created_by: LS_SERVICE.get("staff_id"),
      updated_by: LS_SERVICE.get("staff_id"),
    };

    const Params = { where_condition: this.buildWhereCondition(), mother_fetus_data };
    try {
      await Axios.post(Save_Mother_Fetus_Data, Params);
      notification.success({ message: "Fetus data saved successfully", placement: "topRight" });
      await this.setState({ spin_loading: false });
      // Refresh the parent BEFORE closing so the tables reflect the new records.
      if (this.props.onSaved) {
        await this.props.onSaved();
      }
      if (this.props.onClose) this.props.onClose();
    } catch (err) {
      notification.error({ message: "Something went wrong! Unable to save the data", placement: "topRight" });
      await this.setState({ spin_loading: false });
    }
  };

  renderFetusBlock = (fetus, index) => {
    const { allowEdit } = this.state;
    // Only flag FHR as an error once the user has attempted to save.
    const fhrEmpty = (fetus.fetal_heart_rate === null || fetus.fetal_heart_rate === undefined || String(fetus.fetal_heart_rate).trim() === "");
    const fhrError = this.state.submitted && fhrEmpty;
    return (
      <div className="card p-3 mb-3" key={"fetus-block-" + fetus.fetus_no} style={{ backgroundColor: "#f7fbff" }}>
        <div className="row">
          <div className="col-12">
            <h6 className="text-primary mb-2">{"Fetus " + fetus.fetus_no}</h6>
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <div className="antSelectItem hasdata">
              <Form.Item label="Fetal Movement">
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  optionFilterProp="children"
                  value={fetus.fetal_movement}
                  disabled={!allowEdit}
                  onChange={this.handleSelectChange(index, "fetal_movement")}
                >
                  {Object.keys(FETAL_MOVEMENT).map((d, i) => (
                    <Option key={i} value={d} label={FETAL_MOVEMENT[d]}>{FETAL_MOVEMENT[d]}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div className="col-3">
            <div className="antInputItem hasdata">
              <Form.Item
                label="Fetal Heart Rate (b/min)"
                required
                validateStatus={fhrError ? "error" : ""}
                help={fhrError ? "Fetal Heart Rate is required" : null}
              >
                <Input
                  autoComplete="off"
                  type="text"
                  maxLength="3"
                  disabled={!allowEdit}
                  value={fetus.fetal_heart_rate}
                  onChange={this.handleFieldChange(index, "fetal_heart_rate")}
                />
              </Form.Item>
            </div>
          </div>
          <div className="col-3">
            <div className="antSelectItem hasdata">
              <Form.Item label="Presentation">
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  optionFilterProp="children"
                  value={fetus.presentation}
                  disabled={!allowEdit}
                  onChange={this.handleSelectChange(index, "presentation")}
                >
                  {Object.keys(PRESENTATION).map((d, i) => (
                    <Option key={i} value={d} label={PRESENTATION[d]}>{PRESENTATION[d]}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div className="col-3">
            <div className="antSelectItem hasdata">
              <Form.Item label="Relation of PP to Brim">
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  optionFilterProp="children"
                  value={fetus.relationship_of_pp_to_brim}
                  disabled={!allowEdit}
                  onChange={this.handleSelectChange(index, "relationship_of_pp_to_brim")}
                >
                  {Object.keys(RELATIONSHIP_TO_PP_BRIM).map((d, i) => (
                    <Option key={i} value={d} label={RELATIONSHIP_TO_PP_BRIM[d]}>{RELATIONSHIP_TO_PP_BRIM[d]}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-3">
            <div className="antInputItem hasdata">
              <Form.Item label="Head CC (cm)">
                <Input autoComplete="off" type="text" maxLength="4" disabled={!allowEdit} value={fetus.head_cc} onChange={this.handleFieldChange(index, "head_cc")} />
              </Form.Item>
            </div>
          </div>
          <div className="col-3">
            <div className="antInputItem hasdata">
              <Form.Item label="Abdominal CC (cm)">
                <Input autoComplete="off" type="text" maxLength="4" disabled={!allowEdit} value={fetus.abdominal_cc} onChange={this.handleFieldChange(index, "abdominal_cc")} />
              </Form.Item>
            </div>
          </div>
          <div className="col-3">
            <div className="antInputItem hasdata">
              <Form.Item label="Femur Length (cm)">
                <Input autoComplete="off" type="text" maxLength="4" disabled={!allowEdit} value={fetus.femur_length} onChange={this.handleFieldChange(index, "femur_length")} />
              </Form.Item>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { visible, onClose } = this.props;
    const { allowEdit, fetuses } = this.state;
    return (
      <Modal
        title="Fetus Details"
        visible={visible}
        onCancel={onClose}
        width="1100px"
        className="emrfrm"
        footer={false}
        destroyOnClose={true}
      >
        <Form layout="vertical" className="emrfrm" ref={this.formRef} onFinish={this.onFinish}>
          {(fetuses || []).map((fetus, index) => this.renderFetusBlock(fetus, index))}
          <div className="row mt-3 mb-2">
            <div className="col-12 text-center">
              <Form.Item>
                <button htmlType="submit" className="btn btn-primary px-5" disabled={this.state.spin_loading || !allowEdit}>
                  SAVE {this.state.spin_loading ? LOADER : null}
                </button>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    );
  }
}

export default FetusModal;
